import { useEffect } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import { stripCmSubLayerSuffix } from "../useCmTableMapLayers";

/** A map overlay layer we control, tagged with its MapLibre render type for ranking. */
export interface ManagedLayer {
  id: string;
  /** MapLibre render type: "raster" | "raster-dem" | "fill" | "line" | "circle" | "symbol". */
  type: string;
}

/**
 * Base render hierarchy (bottom -> top): Raster -> Polygon (fill) -> Line String (line) ->
 * Point (circle/symbol). The base map style sits below all of these. Higher rank => drawn later
 * => appears on top.
 */
export const GEOMETRY_PAINT_RANK: Record<string, number> = {
  raster: 0,
  "raster-dem": 0,
  fill: 1,
  line: 2,
  circle: 3,
  symbol: 4
};

/**
 * Rank of a MapLibre layer type in the paint hierarchy. Exported so surfaces that
 * mount layers *without* this hook — the admin boundary preview, for one — can
 * order their own fragments by the same rule instead of inventing a second one.
 */
export const rankOf = (type: string): number => GEOMETRY_PAINT_RANK[type] ?? GEOMETRY_PAINT_RANK.fill;

/**
 * Imperatively enforces the map's layer stacking order via `map.moveLayer()`.
 *
 * WHY: react-map-gl does not reorder already-mounted layers when their JSX order changes — its
 * <Layer> only calls `map.moveLayer` when the `beforeId` prop changes. Reordering the children
 * array therefore has no effect on the live map. We centralise ordering here instead.
 *
 * ORDER (bottom -> top): Base map -> Raster -> Polygon -> Line String -> Point, with the currently
 * active layer (the selected endpoint's layer, including an active spatial-dataset overlay) always
 * lifted to the very top so overlapping features from other layers never hide it and it stays
 * interactable. Sub-layers of a mixed-geometry CM table (id::line / id::fill) resolve to their base
 * id, so the whole table lifts together while keeping its internal fill < line < point order.
 *
 * @param mapRef        react-map-gl map ref.
 * @param managedLayers Overlay layers currently mounted (config rasters, GeoJSON vectors, spatial).
 * @param activeLayerId Canonical id of the active layer, or null when nothing is selected.
 * @param mapReady      True once the map's `load` event has fired. The effect must re-run at that
 *                      point: on the first render `mapRef.current` is still null, and the layer set
 *                      is often already stable, so without this signal the effect would early-return
 *                      and never attach its listeners — leaving the base hierarchy unenforced until
 *                      the user happens to make a selection.
 */
export function useMapLayerZOrder(
  mapRef: React.RefObject<MapRef | null>,
  managedLayers: ManagedLayer[],
  activeLayerId: string | null,
  mapReady: boolean
): void {
  // Stable primitive key so the effect only re-runs when the layer set or types actually change.
  const orderKey = managedLayers.map((l) => `${l.id}:${l.type}`).join("|");

  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap?.();
    if (!map) return;

    const computeDesiredOrder = (): string[] =>
      managedLayers
        .map((layer, index) => {
          const isActive = activeLayerId ? layer.id === activeLayerId || stripCmSubLayerSuffix(layer.id) === activeLayerId : false;
          return { id: layer.id, index, isActive, rank: rankOf(layer.type) };
        })
        .sort((a, b) => {
          // Active layer group is rendered last (on top of everything else).
          if (a.isActive !== b.isActive) return a.isActive ? 1 : -1;
          // Otherwise follow the geometry hierarchy: raster -> fill -> line -> point.
          if (a.rank !== b.rank) return a.rank - b.rank;
          // Stable within the same rank to preserve the incoming layer order.
          return a.index - b.index;
        })
        .map((entry) => entry.id);

    const applyOrder = () => {
      if (!map.isStyleLoaded?.()) return;

      const desired = computeDesiredOrder().filter((id) => map.getLayer(id));
      if (desired.length < 2) return;

      // Lightweight order snapshot (avoids the heavy map.getStyle() serialisation).
      const current = map.getLayersOrder();
      const positions = desired.map((id) => current.indexOf(id));
      const alreadySorted = positions.every((pos, i) => i === 0 || positions[i - 1] < pos);
      // Skip when already correct so our own moveLayer() calls don't re-trigger a reorder loop.
      if (alreadySorted) return;

      // Enforce relative order top-down: place each layer just beneath the one above it.
      for (let i = desired.length - 2; i >= 0; i--) {
        try {
          map.moveLayer(desired[i], desired[i + 1]);
        } catch {
          // A layer may have been removed mid-pass; ignore and let the next apply fix it.
        }
      }
    };

    applyOrder();
    // Layers are added asynchronously by react-map-gl; re-apply when the style/sources change.
    map.on("styledata", applyOrder);
    map.on("sourcedata", applyOrder);
    return () => {
      map.off("styledata", applyOrder);
      map.off("sourcedata", applyOrder);
    };
  }, [mapRef, orderKey, activeLayerId, mapReady]);
}
